import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { invalidateQuery, useApiQuery } from '../../../../base/api/queryUtils.ts'
import useAuth from '../../../../base/hooks/useAuth'
import {
  compareVersions,
  EscposDocument,
  FilePrintOptions,
  JobInfo,
  LicenseStatus,
  PingResult,
  PrinterInfo,
  PrintResult,
} from '../sdk/isi-printer-client.ts'
import {
  getUltimoLoginIsiPrint,
  ISI_PRINT_DESCARGAS_URL,
  ISI_PRINT_PANEL_URL,
  ISI_PRINT_URL,
  IsiPrintCoincidencia,
  IsiPrintEnvioOpciones,
  IsiPrintEstado,
  IsiPrinterStatus,
  isiPrint,
  isiPrintTarget,
  leerManifiestoIsiPrint,
  mensajeIsiPrint,
  resolverImpresoraIsiPrint,
} from '../services/isiPrintClient.ts'
import { ISI_PRINT_SOPORTADO, MENSAJE_ISI_PRINT_NO_SOPORTADO } from '../services/dispositivo.ts'
import { huboCambiosRed, sincronizarImpresorasRed } from '../services/impresoraRed.ts'

export const KEY_ISI_PRINT_DIAGNOSTICO = 'isi-print-diagnostico'
export const KEY_ISI_PRINT_MANIFIESTO = 'isi-print-manifiesto'
export const KEY_ISI_PRINT_IMPRESORAS = 'isi-print-impresoras'

/** Resultado de verificar isi-print en este equipo */
export interface IsiPrintDiagnostico {
  /** Estado sin considerar la versión mínima (eso lo agrega el hook con el manifiesto) */
  estado: Exclude<IsiPrintEstado, 'VERIFICANDO' | 'ACTUALIZACION_REQUERIDA'>
  ping: PingResult
  licencia: LicenseStatus | null
  impresoras: PrinterInfo[]
  /** Motivo legible cuando no está LISTO */
  detalle: string | null
  /** Error al entregar a isi-print las impresoras de red de la sucursal; null si se sincronizaron */
  errorRed?: string | null
}

/**
 * Entrega a isi-print las impresoras de red de la sucursal (solo si cambiaron o al agente le faltan)
 * y devuelve la lista actualizada del agente. No lanza error: lo informa en `errorRed`.
 */
const sincronizarRed = async (
  codigoSucursal: number,
  impresoras: PrinterInfo[],
): Promise<{ impresoras: PrinterInfo[]; errorRed: string | null }> => {
  try {
    const r = await sincronizarImpresorasRed(codigoSucursal, { enAgente: impresoras })
    return { impresoras: huboCambiosRed(r) ? await isiPrint.getPrinters() : impresoras, errorRed: null }
  } catch (e) {
    return {
      impresoras,
      errorRed: `No se pudieron entregar a isi-print las impresoras de red de la sucursal: ${mensajeIsiPrint(e)}`,
    }
  }
}

/**
 * Flujo de inicio: agente instalado → sitio autorizado → licencia vigente (la instala o renueva
 * con `loginIsiPrint`) → impresoras de red de la sucursal sincronizadas → impresoras detectadas.
 * Nunca lanza error.
 * Si el sitio no está autorizado, se entrega la licencia: sus dominios firmados (ISI_PRINT_ORIGINS)
 * lo autorizan sin el diálogo del cajero. Solo si el dominio no está en la licencia queda pendiente.
 * @param licenciaComercio `li.activo` de la sesión: sin licencia de impresión no se pide token.
 * @param codigoSucursal Sucursal de la sesión, para sincronizar sus impresoras de red (null = no sincroniza)
 */
const diagnosticar = async (
  licenciaComercio: boolean,
  codigoSucursal: number | null,
): Promise<IsiPrintDiagnostico> => {
  // Teléfonos y tabletas: no hay agente que consultar
  if (!ISI_PRINT_SOPORTADO) {
    return {
      ping: { online: false } as PingResult,
      licencia: null,
      impresoras: [],
      estado: 'NO_SOPORTADO',
      detalle: MENSAJE_ISI_PRINT_NO_SOPORTADO,
    }
  }
  let ping = await isiPrint.ping()
  const base = { ping, licencia: null, impresoras: [] as PrinterInfo[] }
  if (!ping.online) return { ...base, estado: 'NO_INSTALADO', detalle: null }
  if (!ping.authorized && licenciaComercio) {
    try {
      await isiPrint.ensureLicense(true)
      ping = await isiPrint.ping()
      base.ping = ping
    } catch {
      // El dominio no está en la licencia: queda la autorización manual
    }
  }
  if (!ping.authorized) {
    return {
      ...base,
      estado: 'PENDIENTE_AUTORIZAR',
      detalle: licenciaComercio ? 'Este dominio no está incluido en la licencia de isi-print.' : null,
    }
  }
  if (!licenciaComercio) {
    return {
      ...base,
      estado: 'SIN_LICENCIA',
      detalle: 'El comercio no cuenta con una licencia de impresión activa. Solicítela a su proveedor.',
    }
  }
  let licencia: LicenseStatus
  try {
    licencia = await isiPrint.ensureLicense()
  } catch (e) {
    return { ...base, estado: 'SIN_LICENCIA', detalle: mensajeIsiPrint(e) }
  }
  if (!licencia.valid) return { ...base, licencia, estado: 'SIN_LICENCIA', detalle: licencia.message }
  try {
    const impresoras = await isiPrint.getPrinters()
    const red =
      codigoSucursal == null
        ? { impresoras, errorRed: null }
        : await sincronizarRed(codigoSucursal, impresoras)
    return { ping, licencia, ...red, estado: 'LISTO', detalle: null }
  } catch (e) {
    return { ...base, licencia, estado: 'LISTO', detalle: mensajeIsiPrint(e) }
  }
}

/** Invalida (vuelve a verificar) isi-print desde cualquier lugar */
export const invalidateIsiPrint = () => invalidateQuery(KEY_ISI_PRINT_DIAGNOSTICO)

/** Estado de una impresora guardada (ID, nombre o IP) respecto a lo que detecta el agente */
export interface IsiPrintImpresoraEstado {
  impresora: PrinterInfo | null
  /** 'id' = guardada por ID; 'nombre' / 'puerto' = registro antiguo que conviene actualizar */
  coincidencia: IsiPrintCoincidencia | null
  /** Estado del agente; 'no-encontrada' si no está; 'red' si es una IP que el agente no lista */
  status: IsiPrinterStatus | 'no-encontrada' | 'red' | 'desconocido'
}

export interface UseIsiPrintOptions {
  /** Verifica el agente al montar. @default true */
  habilitado?: boolean
}

/**
 * Hook central de isi-print: estado del agente, licencia, impresoras detectadas (con su ID de
 * hardware), versión publicada e impresión. Comparte en caché la verificación entre componentes.
 * @example
 * const isi = useIsiPrint()
 * isi.estado            // 'NO_INSTALADO' | 'PENDIENTE_AUTORIZAR' | 'SIN_LICENCIA' | 'LISTO' ...
 * isi.impresoras        // [{ id: 'hw-usb001-a3f2', name: 'EPSON TM-T20II', status: 'online' }]
 * await isi.printFile(blob, impresora.atributo1, { copies: 2 }, 'factura.pdf')
 */
export const useIsiPrint = ({ habilitado = true }: UseIsiPrintOptions = {}) => {
  const { user, li } = useAuth()
  const licenciaComercio = Boolean(li?.activo)
  // 0 es casa matriz
  const codigoSucursal = user?.sucursal?.codigo ?? null
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => [KEY_ISI_PRINT_DIAGNOSTICO, ISI_PRINT_URL, licenciaComercio, codigoSucursal],
    [licenciaComercio, codigoSucursal],
  )

  const query = useApiQuery(KEY_ISI_PRINT_DIAGNOSTICO, () => diagnosticar(licenciaComercio, codigoSucursal), {
    enabled: habilitado && Boolean(user) && ISI_PRINT_SOPORTADO,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: true,
    // Mientras el cajero no autoriza el sitio, se vuelve a verificar sola
    refetchInterval: (q) => (q.state.data?.estado === 'PENDIENTE_AUTORIZAR' ? 3_000 : false),
    queryKeys: queryKey.slice(1),
  })

  const manifiesto = useApiQuery(KEY_ISI_PRINT_MANIFIESTO, (ctx) => leerManifiestoIsiPrint(ctx.signal), {
    enabled: habilitado && ISI_PRINT_SOPORTADO,
    staleTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const diag = query.data
  const version = diag?.ping.version
  const m = manifiesto.data

  const actualizacion = useMemo(
    () => ({
      latestVersion: m?.version ?? null,
      /** Instalador; si no se pudo leer el manifiesto, la carpeta de descargas */
      downloadUrl: m?.url ?? ISI_PRINT_DESCARGAS_URL,
      notes: m?.notes ?? null,
      publishedAt: m?.published_at ?? null,
      size: m?.size ?? null,
      sha256: m?.sha256 ?? null,
      minVersion: m?.min_version ?? null,
      updateAvailable: !!version && !!m?.version && compareVersions(m.version, version) > 0,
      updateRequired: !!version && !!m?.min_version && compareVersions(m.min_version, version) > 0,
    }),
    [m, version],
  )

  const estado: IsiPrintEstado = !ISI_PRINT_SOPORTADO
    ? 'NO_SOPORTADO'
    : !diag
      ? 'VERIFICANDO'
      : diag.ping.online && actualizacion.updateRequired
        ? 'ACTUALIZACION_REQUERIDA'
        : diag.estado

  const impresoras = useMemo(() => diag?.impresoras ?? [], [diag?.impresoras])

  /** Verificación reciente (por defecto de hace menos de 5 s), para decidir antes de imprimir */
  const obtenerDiagnostico = useCallback(
    (maxEdadMs = 5_000) =>
      queryClient.fetchQuery({
        queryKey,
        queryFn: () => diagnosticar(licenciaComercio, codigoSucursal),
        staleTime: maxEdadMs,
      }),
    [queryClient, queryKey, licenciaComercio, codigoSucursal],
  )

  const estadoImpresora = useCallback(
    (valor?: string | null, lista: PrinterInfo[] = impresoras): IsiPrintImpresoraEstado => {
      if (!valor?.trim()) return { impresora: null, coincidencia: null, status: 'desconocido' }
      const r = resolverImpresoraIsiPrint(lista, valor)
      if (r) return { impresora: r.impresora, coincidencia: r.coincidencia, status: r.impresora.status }
      if (isiPrintTarget(valor).connection === 'network') {
        return { impresora: null, coincidencia: null, status: 'red' }
      }
      return { impresora: null, coincidencia: null, status: 'no-encontrada' }
    },
    [impresoras],
  )

  const printFile = useCallback(
    (
      blob: Blob,
      impresora: string,
      options?: FilePrintOptions,
      filename?: string,
      envio?: IsiPrintEnvioOpciones,
    ): Promise<PrintResult> => isiPrint.printFile(blob, isiPrintTarget(impresora, envio), options, filename),
    [],
  )
  const printUrl = useCallback(
    (
      url: string,
      impresora: string,
      options?: FilePrintOptions,
      envio?: IsiPrintEnvioOpciones,
    ): Promise<PrintResult> => isiPrint.printUrl(url, isiPrintTarget(impresora, envio), options),
    [],
  )
  const printEscpos = useCallback(
    (impresora: string, doc: EscposDocument, envio?: IsiPrintEnvioOpciones): Promise<PrintResult> =>
      isiPrint.printEscpos(isiPrintTarget(impresora, envio), doc),
    [],
  )
  const getJob = useCallback((jobId: string): Promise<JobInfo> => isiPrint.getJob(jobId), [])
  const cancelJob = useCallback((jobId: string): Promise<JobInfo> => isiPrint.cancelJob(jobId), [])
  const listJobs = useCallback((): Promise<JobInfo[]> => isiPrint.listJobs(), [])
  const printTestPage = useCallback(
    (
      impresora: string,
      format: 'roll' | 'letter' = 'roll',
      paperSize?: '80mm' | '58mm',
    ): Promise<PrintResult> => isiPrint.printTestPage(isiPrintTarget(impresora), format, paperSize),
    [],
  )

  /**
   * Entrega ahora a isi-print las impresoras de red de la sucursal (después de registrar, editar o
   * eliminar una) y vuelve a verificar. Las demás cajas las toman en su próxima verificación.
   * Lanza error si el backend o el agente fallan.
   */
  const refetchDiagnostico = query.refetch
  const sincronizarImpresorasRedAhora = useCallback(async () => {
    if (codigoSucursal == null) return null
    const r = await sincronizarImpresorasRed(codigoSucursal, { forzar: true })
    await refetchDiagnostico()
    return r
  }, [codigoSucursal, refetchDiagnostico])

  return {
    estado,
    listo: estado === 'LISTO',
    /** Hay una verificación en curso */
    verificando: query.isFetching,
    /** Motivo legible cuando no está LISTO */
    detalle: diag?.detalle ?? null,
    /** Error al sincronizar las impresoras de red de la sucursal (ej. isi-print anterior a v1.3.2) */
    errorRed: diag?.errorRed ?? null,
    version: version ?? null,
    os: diag?.ping.os ?? null,
    /** Licencia instalada en el agente */
    licencia: diag?.licencia ?? null,
    /** Licencia del comercio según el backend (última respuesta de loginIsiPrint) */
    licenciaComercio: getUltimoLoginIsiPrint(),
    /** `li.activo` de la sesión */
    licenciaProductoActiva: licenciaComercio,
    impresoras,
    actualizacion,
    manifiestoError: manifiesto.error,
    panelUrl: ISI_PRINT_PANEL_URL,
    refrescar: () => query.refetch(),
    obtenerDiagnostico,
    estadoImpresora,
    printFile,
    printUrl,
    printEscpos,
    printTestPage,
    sincronizarImpresorasRed: sincronizarImpresorasRedAhora,
    getJob,
    cancelJob,
    listJobs,
    /**
     * Pide un token nuevo al backend (`loginIsiPrint`) y lo entrega al agente, aunque el actual siga
     * vigente. Sirve para aplicar dominios agregados a ISI_PRINT_ORIGINS o una licencia renovada.
     */
    renovarLicencia: async () => {
      const r = await isiPrint.ensureLicense(true)
      await query.refetch()
      return r
    },
    abrirPanel: () => window.open(ISI_PRINT_PANEL_URL, '_blank', 'noopener,noreferrer'),
  }
}

export type UseIsiPrintResult = ReturnType<typeof useIsiPrint>

export interface UseIsiPrintImpresoraOptions {
  /**
   * Vigila la impresora: consulta `getPrinters()` cada `intervaloMs` mientras NO esté lista
   * (apagada, sin papel, no encontrada...) y se detiene sola cuando vuelve a estar online.
   * @default false
   */
  vigilar?: boolean
  /** @default 2000 */
  intervaloMs?: number
  /** Se llama una vez cuando la impresora pasa de no lista a lista */
  onLista?: () => void
}

/**
 * Estado en vivo de una impresora guardada (ID de isi-print, nombre o IP) con TanStack Query.
 * La consulta `isi-print-impresoras` se comparte entre todos los que vigilan (TanStack usa el
 * intervalo más corto) y actualiza las impresoras del diagnóstico, para que chips y listados
 * reflejen el cambio sin esperar su refresco.
 * @example
 * const { online, status } = useIsiPrintImpresora(asignacion.nombreImpresora, {
 *   vigilar: motivo === 'impresora-no-disponible',
 *   onLista: () => toast.success('La impresora está lista'),
 * })
 */
export const useIsiPrintImpresora = (
  valor?: string | null,
  { vigilar = false, intervaloMs = 2_000, onLista }: UseIsiPrintImpresoraOptions = {},
) => {
  const isi = useIsiPrint()
  const queryClient = useQueryClient()
  const habilitado = vigilar && isi.listo && Boolean(valor?.trim())

  const query = useApiQuery(KEY_ISI_PRINT_IMPRESORAS, () => isiPrint.getPrinters(), {
    enabled: habilitado,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    // Se detiene sola cuando la impresora vuelve a estar lista
    refetchInterval: (q) => {
      const r = q.state.data ? resolverImpresoraIsiPrint(q.state.data, valor) : null
      return r?.impresora.status === 'online' ? false : intervaloMs
    },
    queryKeys: [ISI_PRINT_URL],
  })

  // Las impresoras recién consultadas reemplazan a las del diagnóstico (chips, listados, provider)
  useEffect(() => {
    if (!query.data) return
    queryClient.setQueriesData<IsiPrintDiagnostico>({ queryKey: [KEY_ISI_PRINT_DIAGNOSTICO] }, (old) =>
      old && old.estado === 'LISTO' ? { ...old, impresoras: query.data!, detalle: null } : old,
    )
  }, [query.data, queryClient])

  const lista = query.data ?? isi.impresoras
  const estado = isi.estadoImpresora(valor, lista)
  const online = estado.status === 'online'

  // Aviso de transición: de no lista a lista
  const anterior = useRef<boolean | null>(null)
  const onListaRef = useRef(onLista)
  onListaRef.current = onLista
  useEffect(() => {
    if (!habilitado) {
      anterior.current = null
      return
    }
    if (anterior.current === false && online) onListaRef.current?.()
    anterior.current = online
  }, [online, habilitado])

  return {
    ...estado,
    /** La impresora está lista para imprimir */
    online,
    /** Se está vigilando (consultas periódicas activas) */
    vigilando: habilitado && !online,
    verificando: query.isFetching,
    refrescar: () => query.refetch(),
  }
}

export default useIsiPrint
